import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkYoutube from 'remark-youtube';
import rehypeRaw from 'rehype-raw';
import { MapPin, Award, Users, Quote, ArrowLeft, ArrowRight, Medal, Calendar, Building, ExternalLink, Trophy } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { teamPerformance } from '../data/mock';
import { getOptimizedImageUrl } from '../lib/utils';

const TeamYearPage = () => {
  const { year } = useParams();
  const yearNum = parseInt(year);

  // Find the team data for this year
  const team = teamPerformance.find(t => t.year === yearNum);

  // If year not found, redirect to performance page
  if (!team) {
    return <Navigate to="/performance" replace />;
  }

  // Find previous and next years for navigation
  const sortedYears = teamPerformance.map(t => t.year).sort((a, b) => b - a);
  const currentIndex = sortedYears.indexOf(yearNum);
  const prevYear = currentIndex < sortedYears.length - 1 ? sortedYears[currentIndex + 1] : null;
  const nextYear = currentIndex > 0 ? sortedYears[currentIndex - 1] : null;

  return (
    <div className="min-h-screen bg-cream" data-testid={`team-year-page-${year}`}>
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={getOptimizedImageUrl(team.image, 1920)}
              srcSet={`
                ${getOptimizedImageUrl(team.image, 800)} 800w,
                ${getOptimizedImageUrl(team.image, 1200)} 1200w,
                ${getOptimizedImageUrl(team.image, 1920)} 1920w
              `}
              sizes="100vw"
              alt={team.team}
              className="w-full h-full object-cover"
              style={{ objectPosition: team.imagePosition || 'center center' }}
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-midnight/80 via-midnight/70 to-midnight" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Top Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <Link
                to="/performance"
                className="inline-flex items-center gap-2 text-pragmatic hover:text-white transition-colors"
              >
                <ArrowLeft size={16} />
                Back to All Years
              </Link>

              <div className="flex items-center gap-4">
                {prevYear && (
                  <Link
                    to={`/performance/${prevYear}`}
                    className="inline-flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all text-sm"
                  >
                    <ArrowLeft size={14} />
                    {prevYear}
                  </Link>
                )}
                {nextYear && (
                  <Link
                    to={`/performance/${nextYear}`}
                    className="inline-flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all text-sm"
                  >
                    {nextYear}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>

            <div className="max-w-3xl">
              {/* Year Badge */}
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-dwarfpony to-dwarfpony-dark text-white font-bold px-6 py-3 rounded-full text-2xl mb-6 shadow-xl">
                <Trophy size={24} />
                {team.year}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                {team.team}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
                  <MapPin size={18} className="text-pragmatic" />
                  <span className="text-white">{team.location}</span>
                </div>
                <div className="bg-gradient-to-r from-richolive to-richolive-dark text-white font-bold px-4 py-2 rounded-full">
                  {team.rank}
                </div>
              </div>

              <p className="text-xl text-pragmatic leading-relaxed">
                {team.school}
              </p>
            </div>
          </div>
        </section>

        {/* Details Section */}
        <section className="py-16 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Problem Statement */}
                <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-md">
                  <h2 className="text-xl font-bold text-deepwalnut mb-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center">
                      <Award className="w-5 h-5 text-dwarfpony" />
                    </div>
                    Challenge Theme
                  </h2>
                  <div className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-xl p-6 border border-pragmatic/20">
                    <p className="text-dwarfpony text-sm font-semibold uppercase tracking-wider mb-2">FIRST Global {team.year}</p>
                    <p className="text-deepwalnut text-2xl font-bold">{team.problemStatement}</p>
                  </div>
                </div>

                {/* Quote / Team Story */}
                <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-md">
                  <h2 className="text-xl font-bold text-deepwalnut mb-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center">
                      <Quote className="w-5 h-5 text-dwarfpony" />
                    </div>
                    Team Story
                  </h2>

                  {team.story ? (
                    <div className="prose prose-stone max-w-none text-heavyrain [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:shadow-lg [&_iframe]:border [&_iframe]:border-pragmatic/20">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkYoutube]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          p: ({ node, ...props }) => <p className="mb-4 leading-relaxed text-lg" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-bold text-deepwalnut" {...props} />,
                          em: ({ node, ...props }) => <em className="italic text-deepwalnut" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-2 marker:text-dwarfpony" {...props} />,
                          li: ({ node, ...props }) => <li className="text-heavyrain" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-xl font-bold text-deepwalnut mt-6 mb-3" {...props} />,
                          blockquote: ({ node, ...props }) => (
                            <blockquote className="relative pl-6 border-l-4 border-dwarfpony/50 italic text-deepwalnut/90 my-6 py-2 bg-pragmatic/5 rounded-r-lg" {...props} />
                          ),
                          a: ({ node, ...props }) => (
                            <a
                              {...props}
                              className="text-dwarfpony hover:text-richolive underline underline-offset-4 transition-colors font-medium"
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          ),
                        }}
                      >
                        {team.story}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <blockquote className="relative">
                      <Quote className="absolute -top-2 -left-2 w-8 h-8 text-pragmatic/50" />
                      <p className="text-heavyrain text-lg italic pl-8 leading-relaxed">
                        "{team.quote}"
                      </p>
                    </blockquote>
                  )}
                </div>

                {/* Medals */}
                {team.medals && team.medals.length > 0 && (
                  <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-md">
                    <h2 className="text-xl font-bold text-deepwalnut mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center">
                        <Medal className="w-5 h-5 text-dwarfpony" />
                      </div>
                      Awards & Recognition
                    </h2>
                    <div className="flex flex-wrap gap-3">
                      {team.medals.map((medal, i) => (
                        <div
                          key={i}
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-pragmatic/30 to-dwarfpony/20 text-dwarfpony-dark px-4 py-2 rounded-full text-sm font-medium border border-dwarfpony/30"
                        >
                          <Medal size={16} />
                          {medal}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Image Gallery */}
                <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-md">
                  <h2 className="text-xl font-bold text-deepwalnut mb-4">Gallery</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {team.images && team.images.length > 0 ? (
                      team.images.map((img, i) => (
                        <div key={i} className="aspect-video rounded-xl overflow-hidden shadow-sm border border-pragmatic/20">
                          <img
                            src={getOptimizedImageUrl(img, 800)}
                            srcSet={`
                              ${getOptimizedImageUrl(img, 400)} 400w,
                              ${getOptimizedImageUrl(img, 800)} 800w
                            `}
                            sizes="(max-width: 640px) 100vw, 33vw"
                            alt={`${team.team} - ${i + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ))
                    ) : (
                      <>
                        <div className="aspect-video rounded-xl overflow-hidden shadow-sm border border-pragmatic/20">
                          <img
                            src={getOptimizedImageUrl(team.image, 800)}
                            alt={`${team.team} - 1`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-pragmatic/20 to-richolive/10 flex items-center justify-center border border-pragmatic/30">
                          <p className="text-heavyrain text-sm">More photos coming soon</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Team Members */}
                {team.students && team.students.length > 0 && (
                  <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                    <h3 className="text-lg font-bold text-deepwalnut mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-dwarfpony" />
                      Team Members
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {team.students.map((student, i) => (
                        <span
                          key={i}
                          className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 text-deepwalnut px-3 py-1.5 rounded-full text-sm border border-pragmatic/20"
                        >
                          {student}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                  <h3 className="text-lg font-bold text-deepwalnut mb-4">Quick Facts</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-dwarfpony" />
                      <div>
                        <p className="text-heavyrain text-xs">Year</p>
                        <p className="text-deepwalnut font-semibold">{team.year}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-dwarfpony" />
                      <div>
                        <p className="text-heavyrain text-xs">Location</p>
                        <p className="text-deepwalnut font-semibold">{team.location}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-dwarfpony" />
                      <div>
                        <p className="text-heavyrain text-xs">School</p>
                        <p className="text-deepwalnut font-semibold">{team.school}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Trophy className="w-5 h-5 text-dwarfpony" />
                      <div>
                        <p className="text-heavyrain text-xs">Ranking</p>
                        <p className="text-deepwalnut font-semibold">{team.rank}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mentors */}
                {team.mentors && team.mentors.length > 0 && (
                  <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                    <h3 className="text-lg font-bold text-deepwalnut mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-dwarfpony" />
                      Mentors
                    </h3>
                    <div className="space-y-2">
                      {team.mentors.map((mentor, i) => (
                        <div key={i} className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-lg px-4 py-2 border border-pragmatic/20">
                          <p className="text-deepwalnut font-medium">{mentor}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supporters */}
                {team.supporters && team.supporters.length > 0 && (
                  <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                    <h3 className="text-lg font-bold text-deepwalnut mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-dwarfpony" />
                      Supporters
                    </h3>
                    <div className="space-y-2">
                      {team.supporters.map((supporter, i) => (
                        <div key={i} className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-lg px-4 py-2 border border-pragmatic/20">
                          <p className="text-deepwalnut font-medium">{supporter}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sponsors */}
                {team.sponsors && team.sponsors.length > 0 && (
                  <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                    <h3 className="text-lg font-bold text-deepwalnut mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-dwarfpony" />
                      Sponsors
                    </h3>
                    <div className="space-y-2">
                      {team.sponsors.map((sponsor, i) => (
                        <div key={i} className="bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-lg px-4 py-2 border border-pragmatic/20">
                          <p className="text-deepwalnut font-medium">{sponsor}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-gradient-to-br from-dwarfpony to-dwarfpony-dark rounded-2xl p-6 text-center">
                  <h3 className="text-white font-bold text-lg mb-2">Join the Team</h3>
                  <p className="text-pragmatic text-sm mb-4">Be part of India's next robotics journey</p>
                  <Link to="/contact">
                    <Button className="w-full bg-white text-dwarfpony hover:bg-cream font-semibold">
                      Apply Now
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Year Navigation */}
        <section className="py-12 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              {prevYear ? (
                <Link
                  to={`/performance/${prevYear}`}
                  className="flex items-center gap-3 bg-cream border border-pragmatic/30 rounded-xl px-6 py-4 hover:border-dwarfpony hover:shadow-md transition-all group"
                >
                  <ArrowLeft className="w-5 h-5 text-heavyrain group-hover:text-dwarfpony transition-colors" />
                  <div>
                    <p className="text-heavyrain text-xs">Previous Year</p>
                    <p className="text-deepwalnut font-bold text-lg">{prevYear}</p>
                  </div>
                </Link>
              ) : (
                <div />
              )}

              <Link
                to="/performance"
                className="text-richolive hover:text-dwarfpony transition-colors font-medium"
              >
                View All Years
              </Link>

              {nextYear ? (
                <Link
                  to={`/performance/${nextYear}`}
                  className="flex items-center gap-3 bg-cream border border-pragmatic/30 rounded-xl px-6 py-4 hover:border-dwarfpony hover:shadow-md transition-all group"
                >
                  <div className="text-right">
                    <p className="text-heavyrain text-xs">Next Year</p>
                    <p className="text-deepwalnut font-bold text-lg">{nextYear}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-heavyrain group-hover:text-dwarfpony transition-colors" />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeamYearPage;
